import React from 'react'
import { Link } from 'react-router-dom'
import ReactTimeAgo from 'react-time-ago'
import { Badge, Button, Table } from 'reactstrap'
import styled from 'styled-components'

import { HardwareDto } from '../../hardware.type'
import { useCountries } from '../../hardware.hook'
import { formatNiceName } from '../../hardware.utils'

const CenterContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: 20px;
`

type IComponentProps = {
  data: HardwareDto[] | undefined
  isLoading: boolean
  isError: boolean
  onEdit: (hardware: HardwareDto) => void
  onDelete: (hardware: HardwareDto) => void
}

const HardwareTable: React.FC<IComponentProps> = ({
  data,
  isLoading,
  isError,
  onEdit,
  onDelete,
}) => {
  const { data: countriesData = [], isLoading: countriesLoading } =
    useCountries()

  if (isLoading || countriesLoading) {
    return <div>Loading ...</div>
  }

  if (isError) {
    return <div>Error loading hardware. Please try again.</div>
  }

  if (!data?.length) {
    return (
      <div
        className="table-responsive"
        style={{
          paddingBottom: 550,
          marginBottom: -550,
          msOverflowStyle: 'none',
        }}
      >
        <CenterContainer>No hardware found</CenterContainer>
      </div>
    )
  }

  return (
    <Table className="table table-responsive table-striped mb-0">
      <thead>
        <tr>
          <th>Name</th>
          <th>Hardware Features</th>
          <th>Mintsoft Stock Level</th>
          <th>Available In</th>
          <th>Details</th>
          <th>Last Updated</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {data.map((hardware) => {
          const parentName = hardware.hardwareParentId
            ? data.find((h) => h.id === hardware.hardwareParentId)?.name
            : ''
          const mintsoftStockNotRequired =
            !hardware.countriesAvailableIn.includes('GB')
          return (
            <tr
              key={hardware.id}
              onClick={() => onEdit(hardware)}
              style={{ cursor: 'pointer' }}
            >
              <td>
                <b>{formatNiceName(hardware.name)}</b>
                <br />
                <small>{hardware.name}</small>
              </td>
              <td>
                <div className="d-flex flex-wrap gap-1">
                  {hardware.hardwareFeatures.map((feature) => (
                    <Badge key={feature} className="bg-light">
                      {feature}
                    </Badge>
                  ))}
                </div>
              </td>
              <td>
                {mintsoftStockNotRequired ? 'N/A' : hardware.mintsoftStockLevel}
              </td>
              <td>
                <div className="d-flex flex-wrap gap-1">
                  {hardware.countriesAvailableIn.map((countryCode) => {
                    const country = countriesData.find(
                      (c) => c.code === countryCode,
                    )
                    if (!country) return null
                    return (
                      <Badge key={countryCode} className="bg-light">
                        {country.flag} {country.name}
                      </Badge>
                    )
                  })}
                </div>
              </td>
              <td>
                <div className="d-flex flex-wrap gap-1">
                  {hardware.isDefaultTerminalForCountry && (
                    <Badge className="bg-primary">
                      Default Card Reader For{' '}
                      {hardware.countriesAvailableIn.length > 1
                        ? 'Countries'
                        : 'Country'}
                    </Badge>
                  )}
                  {hardware.isTerminal && (
                    <Badge className="bg-info">Terminal</Badge>
                  )}
                  {hardware.hardwareParentId &&
                    (parentName ? (
                      <Badge className="bg-info">
                        Accessory of {formatNiceName(parentName)}
                      </Badge>
                    ) : (
                      <Badge className="bg-danger">
                        Accessory of missing parent: {hardware.hardwareParentId}
                      </Badge>
                    ))}
                  {hardware.fallbackToStripe && !hardware.stripeSku && (
                    <Badge className="bg-danger">SKU required</Badge>
                  )}
                  {!hardware.fallbackToStripe && !hardware.mintsoftSku && (
                    <Badge className="bg-danger">SKU required</Badge>
                  )}
                  {!hardware.fallbackToStripe && (
                    <Badge className="bg-warning">Mintsoft Only</Badge>
                  )}
                </div>
              </td>
              <td>
                <ReactTimeAgo date={hardware.updatedAt || new Date()} />
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <Button
                  onClick={() => onEdit(hardware)}
                  className="btn btn-primary me-2"
                >
                  Edit
                </Button>
                {/* TODO: once HardwarePricingPage is no longer a placeholder,
                    update this route to be hardware-specific, e.g.
                    `/hardware/pricing/${hardware.id}`. */}
                <Link to="/hardware/pricing" className="btn btn-secondary me-2">
                  View Pricing
                </Link>
                <Button
                  onClick={() => onDelete(hardware)}
                  className="btn btn-danger"
                >
                  Delete
                </Button>
              </td>
            </tr>
          )
        })}
      </tbody>
    </Table>
  )
}

export default HardwareTable
